/**
 * CustomAgTable - Comprehensive AG Grid React Table Component
 * A reusable table component with theming that matches the Reelax Ads Web app design system.
 *
 * @example
 * import { CustomAgTable } from '../components/core_component/CustomAgTable';
 *
 * <CustomAgTable
 *   rowData={data}
 *   columnDefs={columns}
 *   pagination
 *   paginationPageSize={15}
 *   onRowClicked={(row) => console.log(row)}
 * />
 */

import React, { useMemo, useRef, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry, themeMaterial, themeQuartz } from 'ag-grid-community';
import './CustomAgTable.css';
import Button from "./Button";

// Register AG Grid Community modules
ModuleRegistry.registerModules([AllCommunityModule]);

/**
 * Custom theme based on Quartz, styled to match the app's design system
 * Uses CSS variables from public/css/style.css
 */
const reelaxTheme = themeMaterial.withParams({
    // Spacing
    spacing: 8,

    // Colors matching the app's CSS variables
    foregroundColor: '#3a3b43', // --gray-gray-400
    backgroundColor: '#ffffff', // --general-white-color
    headerBackgroundColor: '#f0f3f9', // --general-secondary-color
    headerTextColor: '#5a5b62', // --gray-gray-300

    // Row colors
    rowHoverColor: '#f0f3f9', // --general-secondary-color (hover state)
    oddRowBackgroundColor: '#ffffff',

    // Selection colors
    selectedRowBackgroundColor: '#e8f1fe', // --blue-blue-100

    // Border colors
    borderColor: '#e6e7e8', // --gray-gray-50

    // Fonts
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    headerFontSize: 12,
    headerFontWeight: 600,

    // Border radius
    wrapperBorderRadius: 8,

    // Accent color for interactive elements
    accentColor: '#1977f2', // --blue-blue-500
});

/**
 * CustomAgTable Component
 *
 * @param {Object} props
 * @param {Array} props.rowData - Data to display in the table
 * @param {Array} props.columnDefs - AG Grid column definitions
 * @param {boolean} [props.pagination=true] - Enable pagination
 * @param {number} [props.paginationPageSize=15] - Number of rows per page
 * @param {Array} [props.paginationPageSizeSelector=[10, 15, 25, 50]] - Page size options
 * @param {string} [props.rowSelection] - Row selection mode: 'single', 'multiRow', or undefined for none
 * @param {Function} [props.onRowSelected] - Callback when row selection changes
 * @param {Function} [props.onRowClicked] - Callback when a row is clicked
 * @param {boolean} [props.loading=false] - Show loading overlay

 * @param {boolean} [props.enableFilter=true] - Enable column filtering
 * @param {boolean} [props.enableSort=true] - Enable column sorting
 * @param {boolean} [props.enableFloatingFilter=false] - Show floating filter inputs in header
 * @param {string} [props.noDataMessage='No data to display'] - Empty state message
 * @param {string} [props.className=''] - Additional CSS classes
 * @param {React.Ref} [props.gridRef] - Ref to access AG Grid API
 * @param {Object} [props.defaultColDef] - Default column definitions to merge
 * @param {number} [props.rowHeight] - Custom row height in pixels
 * @param {boolean} [props.serverSidePagination=false] - Enable server-side pagination with client-side sorting
 * @param {Function} [props.onPageChange] - Callback when page changes: (page) => void
 * @param {number} [props.totalRows] - Total number of rows for server-side pagination
 * @param {number} [props.currentPage=1] - Current page number for server-side pagination (1-based)
 *
 * Column-level optional shimmer keys for newly added/in-progress rows:
 * @param {string} [columnDef.newRowShimmerField] - Dot-path for value presence check (fallback: column field)
 * @param {Function} [columnDef.newRowShimmerValueGetter] - Custom value resolver for shimmer presence checks
 * @param {boolean} [columnDef.treatZeroAsMissing=true] - Treat numeric 0 as missing while deciding shimmer
 * @param {string|number} [columnDef.shimmerWidth='80%'] - Custom shimmer width per column
 * @param {string|number} [columnDef.shimmerHeight='20px'] - Custom shimmer height per column
 * @param {string|number} [columnDef.shimmerBorderRadius='4px'] - Custom shimmer border radius per column
 */
const CustomAgTable = ({
    height = 'auto',
    rowData = [],
    columnDefs = [],
    pagination = true,
    paginationPageSize = 15,
    paginationPageSizeSelector = [10, 15, 25, 50],
    rowSelection,
    onRowSelected,
    onRowClicked,
    loading = false,
    enableFilter = true,
    enableSort = true,
    enableFloatingFilter = false,
    noDataMessage = 'No data to display',
    className = '',
    gridRef: externalGridRef,
    defaultColDef: externalDefaultColDef,
    getRowId,
    enableClickSelection = false,
    rowHeight,
    serverSidePagination = false,
    onPageChange,
    totalRows,
    currentPage = 1,
    ...restProps
}) => {
    // Internal ref for grid API
    const internalGridRef = useRef(null);
    const gridRef = externalGridRef || internalGridRef;

    // Merge default column definitions with external ones
    const defaultColDef = useMemo(() => ({
        flex: 1,
        minWidth: 100,
        filter: enableFilter,
        sortable: enableSort,
        unSortIcon: true, // Show icon when not sorted
        floatingFilter: enableFloatingFilter,
        resizable: true,
        suppressMovable: true, // Prevent column reordering
        ...externalDefaultColDef,
    }), [enableFilter, enableSort, enableFloatingFilter, externalDefaultColDef]);

    const getNestedValue = useCallback((obj, path) => {
        if (!obj || !path || typeof path !== 'string') return undefined;
        return path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
    }, []);

    const isMissingValue = useCallback((value, treatZeroAsMissing = true) => {
        if (value === null || value === undefined) return true;
        if (typeof value === 'string') {
            const trimmed = value.trim();
            return trimmed === '' || trimmed === '-' || trimmed === '...';
        }
        if (typeof value === 'number') {
            if (Number.isNaN(value)) return true;
            return treatZeroAsMissing ? value === 0 : false;
        }
        if (Array.isArray(value)) return value.length === 0;
        if (typeof value === 'object') return Object.keys(value).length === 0;
        return false;
    }, []);

    const shouldShowNewRowShimmer = useCallback((params, col) => {
        const row = params?.data;
        if (!row || row?.is_loading_placeholder) return false;

        const isRowLoading = row?.is_newly_added || row?.is_loading_in_progress || row?.__loading_state === 'IN_PROGRESS';
        if (!isRowLoading) return false;

        const treatZeroAsMissing = col?.treatZeroAsMissing ?? true;
        const shimmerFieldPath = col?.newRowShimmerField || col?.shimmerField || col?.field;

        let resolvedValue;
        if (typeof col?.newRowShimmerValueGetter === 'function') {
            resolvedValue = col.newRowShimmerValueGetter(params);
        } else if (shimmerFieldPath) {
            resolvedValue = getNestedValue(row, shimmerFieldPath);
        } else {
            resolvedValue = params?.valueFormatted ?? params?.value;
        }

        return isMissingValue(resolvedValue, treatZeroAsMissing);
    }, [getNestedValue, isMissingValue]);

    // Wrap cellRenderers to automatically handle loading state at table level
    const wrappedColumnDefs = useMemo(() => {
        return columnDefs.map((col) => {
            const hasFunctionRenderer = col.cellRenderer && typeof col.cellRenderer === 'function';
            const hasUnsupportedRenderer = col.cellRenderer && typeof col.cellRenderer !== 'function';

            if (hasUnsupportedRenderer) return col;

            const originalRenderer = hasFunctionRenderer ? col.cellRenderer : null;
            return {
                ...col,
                cellRenderer: (params) => {
                    const showTableLoadingPlaceholder = loading && params?.data?.is_loading_placeholder;
                    const showNewRowShimmer = shouldShowNewRowShimmer(params, col);

                    if (showTableLoadingPlaceholder || showNewRowShimmer) {
                        return (
                            <div
                                className="shimmer"
                                style={{
                                    width: col?.shimmerWidth || '80%',
                                    height: col?.shimmerHeight || '20px',
                                    borderRadius: col?.shimmerBorderRadius || '4px',
                                    margin: '0 auto',
                                }}
                            />
                        );
                    }

                    if (originalRenderer) {
                        const rendered = originalRenderer(params);
                        if (typeof rendered === 'string' && (rendered === 'Request Price' || rendered === 'Award Contract')) {
                            return (
                                <Button
                                    variant="primary"
                                    width={col?.minWidth || 120}
                                    onClick={() => { /* action should be handled by original component/context */ }}
                                >
                                    {rendered}
                                </Button>
                            );
                        }

                        return rendered;
                    }

                    const cellText = params?.valueFormatted ?? params?.value ?? '';
                    if (typeof cellText === 'string' && (cellText === 'Request Price' || cellText === 'Award Contract')) {
                        return (
                            <Button
                                variant="primary"
                                width={col?.minWidth || 120}
                                onClick={() => { /* action to be provided by caller via renderer when needed */ }}
                            >
                                {cellText}
                            </Button>
                        );
                    }

                    return cellText;
                },
            };
        });
    }, [columnDefs, loading, shouldShowNewRowShimmer]);

    // Modify columnDefs to ensure proper column ordering and multi-row selection
    const modifiedColumnDefs = useMemo(() => {
        const columnsToUse = wrappedColumnDefs;

        // If multiRow selection is enabled (checkboxes), reorder columns to ensure
        // checkbox column is always leftmost and other pinned-left columns come after
        if (rowSelection === 'multiRow') {
            // Separate columns into categories: left-pinned and others
            const leftPinnedCols = [];
            const otherCols = [];

            columnsToUse.forEach(col => {
                if (col.pinned === 'left') {
                    leftPinnedCols.push(col);
                } else {
                    otherCols.push(col);
                }
            });

            // Create the checkbox column that will be pinned to leftmost position
            const checkboxCol = {
                headerCheckboxSelection: true,
                checkboxSelection: true,
                pinned: 'left',
                lockPosition: 'left', // Locks this column to the left, preventing it from being moved
                width: 50,
                maxWidth: 50,
                minWidth: 50,
                suppressHeaderMenuButton: true,
                suppressColumnsToolPanel: true,
                resizable: false,
                sortable: false,
                filter: false,
                headerName: '',
                field: '__checkbox__',
                cellRenderer: (params) => {
                    // Don't render anything - AG Grid handles checkbox rendering
                    return null;
                },
            };

            // Return columns in order: checkbox first, then other left-pinned, then rest
            return [checkboxCol, ...leftPinnedCols, ...otherCols];
        }

        return columnsToUse;
    }, [wrappedColumnDefs, rowSelection]);

    // Custom sorting icons
    const icons = useMemo(() => ({
        sortAscending: '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 2L1 6H9L5 2Z" fill="currentColor"/></svg>',
        sortDescending: '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 8L9 4H1L5 8Z" fill="currentColor"/></svg>',
        sortUnSort: '<svg width="10" height="14" viewBox="0 0 10 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 1L1 5H9L5 1Z" fill="currentColor" opacity="0.4"/><path d="M5 13L9 9H1L5 13Z" fill="currentColor" opacity="0.4"/></svg>',
        checkboxChecked: '<svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg"><path d="M5 9 L8 12 L13 6" stroke="white" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>',
        checkboxUnchecked: '<span></span>',
        checkboxIndeterminate: '<svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg"><line x1="5" y1="9" x2="13" y2="9" stroke="white" stroke-width="2.5" stroke-linecap="round"/></svg>',
    }), []);

    // Row selection configuration
    // Note: When multiRow is enabled, we manually add the checkbox column in modifiedColumnDefs
    // to ensure it's always pinned to the leftmost position
    const rowSelectionConfig = useMemo(() => {
        if (!rowSelection) return undefined;

        return {
            mode: rowSelection,
            // Disable automatic checkbox rendering since we manually add it for multiRow
            checkboxes: false,
            headerCheckbox: false,
            enableClickSelection: enableClickSelection,
        };
    }, [rowSelection, enableClickSelection]);

    // Handle row selection change
    const handleSelectionChanged = useCallback((event) => {
        if (onRowSelected) {
            const selectedRows = event.api.getSelectedRows();
            onRowSelected(selectedRows, event);
        }
    }, [onRowSelected]);

    // Handle row click
    const handleRowClicked = useCallback((event) => {
        if (onRowClicked) {
            onRowClicked(event);
        }
    }, [onRowClicked]);

    // Custom loading overlay component (full screen)
    const loadingOverlayComponent = useMemo(() => {
        return () => (
            <div className="custom-ag-loading-overlay" style={{ backgroundColor: 'transparent' }}>
                <div className="custom-ag-loading-spinner"></div>
                <span>Loading...</span>
            </div>
        );
    }, []);

    // Custom loading cell renderer (for infinite scroll individual rows)
    const loadingCellRenderer = useMemo(() => {
        return () => (
            <div className="shimmer" style={{
                width: '80%',
                height: '14px',
                borderRadius: '4px',
                margin: 'auto',
            }} />
        );
    }, []);

    // Custom no rows overlay component
    const noRowsOverlayComponent = useMemo(() => {
        return () => (
            <div className="custom-ag-no-rows-overlay">
                <span>{noDataMessage}</span>
            </div>
        );
    }, [noDataMessage]);

    // Container style with dynamic height
    const containerStyle = useMemo(() => ({
        width: '100%',
    }), []);

    // Determine domLayout and grid height based on height prop
    const hasFixedHeight = height !== 'auto' && height !== undefined && height !== null;
    const domLayout = hasFixedHeight ? 'normal' : 'autoHeight';
    const gridStyle = useMemo(() => (
        hasFixedHeight ? { height, width: '100%' } : { width: '100%' }
    ), [hasFixedHeight, height]);

    // When loading, inject placeholder rows marked with is_loading_placeholder.
    // The wrapped cellRenderers (via wrappedColumnDefs) automatically detect these
    // and display shimmer UI instead of actual content. This ensures consistent
    // table-level loading behavior without requiring individual cell-level checks.
    // Each row carries a unique __placeholder_id so that AG Grid's getRowId never
    // collapses multiple rows into one.
    const displayRowData = useMemo(() => {
        if (restProps.rowModelType === 'infinite') {
            return undefined;
        }
        // Only replace the grid with skeleton rows on the initial/empty load.
        // If we already have row data, keep showing it while `loading` is true
        // (background refetch, add-flow sync) so the table does not flash.
        const hasRows = rowData && rowData.length > 0;
        if (loading && !hasRows) {
            return Array.from({ length: paginationPageSize }, (_, i) => ({ __placeholder_id: `__placeholder_${i}`, is_loading_placeholder: true }));
        }
        return rowData;
    }, [rowData, loading, paginationPageSize, restProps.rowModelType]);

    // Wrap the consumer's getRowId to fall back to __placeholder_id when the
    // real ID is not yet available (i.e. during the loading/shimmer phase).
    const resolvedGetRowId = useMemo(() => {
        if (!getRowId) return undefined;
        return (params) => {
            const id = getRowId(params);
            return id ?? params.data?.__placeholder_id;
        };
    }, [getRowId]);

    // Calculate pagination values
    const totalPages = useMemo(() => {
        if (!serverSidePagination || !totalRows) return 0;
        return Math.ceil(totalRows / paginationPageSize);
    }, [serverSidePagination, totalRows, paginationPageSize]);

    const startRow = useMemo(() => {
        if (!serverSidePagination || !totalRows) return 0;
        // Total rows 1, page size 10, current page 1: result (1-1)*10 + 1 = 1
        return totalRows === 0 ? 0 : (currentPage - 1) * paginationPageSize + 1;
    }, [serverSidePagination, currentPage, paginationPageSize, totalRows]);

    const endRow = useMemo(() => {
        if (!serverSidePagination || !totalRows) return 0;
        return totalRows === 0 ? 0 : Math.min(currentPage * paginationPageSize, totalRows);
    }, [serverSidePagination, currentPage, paginationPageSize, totalRows]);

    // Handle page navigation
    const handlePageChange = useCallback((newPage) => {
        if (onPageChange && newPage >= 1 && (totalPages === 0 || newPage <= totalPages)) {
            onPageChange(newPage);
        }
    }, [onPageChange, totalPages]);

    // Custom Pagination Panel Component (matches AG Grid styling)
    const CustomPaginationPanel = useCallback(() => {
        if (!serverSidePagination) return null;

        const effectiveStartRow = totalRows === 0 ? 0 : startRow;

        return (
            <div className="ag-paging-panel ag-unselectable" role="presentation">
                <span className="ag-paging-row-summary-panel" role="status">
                    <span className="ag-paging-row-summary-panel-number">{effectiveStartRow}</span>
                    {' to '}
                    <span className="ag-paging-row-summary-panel-number">{endRow}</span>
                    {' of '}
                    <span className="ag-paging-row-summary-panel-number">{totalRows || 0}</span>
                </span>
                <span className="ag-paging-page-summary-panel" role="presentation">
                    <div className="ag-paging-button-wrapper" role="presentation">
                        <button
                            type="button"
                            className="ag-paging-button"
                            disabled={currentPage === 1 || loading}
                            onClick={() => handlePageChange(1)}
                            aria-label="First Page"
                        >
                            <span className="ag-icon ag-icon-first" unselectable="on" role="presentation"></span>
                        </button>
                    </div>
                    <div className="ag-paging-button-wrapper" role="presentation">
                        <button
                            type="button"
                            className="ag-paging-button"
                            disabled={currentPage === 1 || loading}
                            onClick={() => handlePageChange(currentPage - 1)}
                            aria-label="Previous Page"
                        >
                            <span className="ag-icon ag-icon-previous" unselectable="on" role="presentation"></span>
                        </button>
                    </div>
                    <span className="ag-paging-description" role="status">
                        <span>Page </span>
                        <span className="ag-paging-number">{totalPages === 0 ? 0 : currentPage}</span>
                        <span> of </span>
                        <span className="ag-paging-number">{totalPages || 0}</span>
                    </span>
                    <div className="ag-paging-button-wrapper" role="presentation">
                        <button
                            type="button"
                            className="ag-paging-button"
                            disabled={currentPage >= totalPages || loading}
                            onClick={() => handlePageChange(currentPage + 1)}
                            aria-label="Next Page"
                        >
                            <span className="ag-icon ag-icon-next" unselectable="on" role="presentation"></span>
                        </button>
                    </div>
                    <div className="ag-paging-button-wrapper" role="presentation">
                        <button
                            type="button"
                            className="ag-paging-button"
                            disabled={currentPage >= totalPages || loading}
                            onClick={() => handlePageChange(totalPages)}
                            aria-label="Last Page"
                        >
                            <span className="ag-icon ag-icon-last" unselectable="on" role="presentation"></span>
                        </button>
                    </div>
                </span>
            </div>
        );
    }, [serverSidePagination, totalRows, startRow, endRow, currentPage, totalPages, loading, handlePageChange]);

    return (
        <div
            className={`custom-ag-table-container ${onRowClicked ? 'clickable-rows' : ''} ${className}`}
            style={containerStyle}
        >
            <div style={gridStyle}>
                <AgGridReact
                    ref={gridRef}
                    theme={reelaxTheme}
                    rowData={displayRowData}
                    columnDefs={modifiedColumnDefs}
                    defaultColDef={defaultColDef}
                    domLayout={domLayout}
                    // State and interaction
                    pagination={serverSidePagination ? false : pagination}
                    paginationPageSize={paginationPageSize}
                    paginationPageSizeSelector={serverSidePagination ? false : paginationPageSizeSelector}

                    // Selection
                    rowSelection={rowSelectionConfig}
                    onSelectionChanged={handleSelectionChanged}
                    onRowClicked={handleRowClicked}

                    // Loading state
                    loading={false}
                    loadingOverlayComponent={loadingOverlayComponent}
                    noRowsOverlayComponent={noRowsOverlayComponent}

                    // Performance optimizations
                    animateRows={true}
                    suppressCellFocus={true}

                    // Icons
                    icons={icons}

                    // Sizing
                    rowHeight={rowHeight}

                    // Loading
                    loadingCellRenderer={loadingCellRenderer}
                    infiniteInitialRowCount={paginationPageSize}
                    suppressRowClickSelection={true}

                    // Pass through any additional props
                    getRowId={resolvedGetRowId}
                    {...restProps}
                />
            </div>
            {serverSidePagination && <CustomPaginationPanel />}
        </div>
    );
};

export default CustomAgTable;
